import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Layout, Tooltip } from "antd";
import { AuthContext, HasRight, Rights } from "../../utilites/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faCalendarAlt,
  faUser,
  faFile,
  faFileInvoiceDollar,
  faList,
  faCheckSquare,
  faThumbtack,
  faSignature,
  faMegaphone,
  faChartPieAlt,
  faCog,
  faChevronDown,
  faUsers,
  faObjectIntersect,
  faTag,
  faCloudArrowDown,
  faMemoCircleInfo,
  faMoneyBill1Wave,
  faLink,
  faListCheck,
} from "@fortawesome/pro-solid-svg-icons";

const { Sider } = Layout;
type Props = {
  isCollapsed: boolean;
};

const defaultProps: Props = {
  isCollapsed: false,
};

const addCheveron = (collapsed, icon) => {
  return (
    <span>
      {icon}
      {collapsed && (
        <FontAwesomeIcon
          icon={faChevronDown}
          className="ChevronRotatable"
          alt="faChevronDown"
          style={{ marginLeft: 10 }}
        />
      )}
    </span>
  );
};

const LeftNav: React.FC<Props> = ({ isCollapsed }) => {
  let [collapsed, setCollapsed] = useState(isCollapsed);
  const [
    attendanceAndActivityFeatureEnabled,
    setAttendanceAndActivityFeatureEnabled,
  ] = useState<boolean>(null);
  const location = useLocation();

  /**
   * Show tooltip for collapse button on left menu
   * @param collapseTooltip
   */
  const showCollapseTooltip = (collapseTooltip) => {
    setTimeout(() => {
      collapseTooltip.style.display = "block";
    }, 500);
  };

  /**
   * Hide tooltip for collapse button on left menu
   * @param collapseTooltip
   */
  const hideCollapseTooltip = (collapseTooltip) => {
    setTimeout(() => {
      collapseTooltip.style.display = "none";
    }, 500);
  };

  /**
   * Toggle collapse when enter is pressed
   * @param e
   * @param siderTriggerElement
   */
  const handleKeyDown = (e, siderTriggerElement) => {
    if (e.key === "Enter") {
      siderTriggerElement.click();
    }
  };

  useEffect(() => {
    // workaround to enable tooltip on collapse button on left menu
    const collapseTooltip = document.querySelector(
      ".collapse-tooltip"
    ) as HTMLElement;
    const siderTriggerElement = document.querySelector(
      ".ant-layout-sider-trigger"
    ) as HTMLElement;

    // enable collapse button to be focused on tab
    siderTriggerElement.tabIndex = 0;

    siderTriggerElement.addEventListener("mouseover", () =>
      showCollapseTooltip(collapseTooltip)
    );
    siderTriggerElement.addEventListener("mouseout", () =>
      hideCollapseTooltip(collapseTooltip)
    );
    siderTriggerElement.addEventListener("focusin", () =>
      showCollapseTooltip(collapseTooltip)
    );
    siderTriggerElement.addEventListener("focusout", () =>
      hideCollapseTooltip(collapseTooltip)
    );
    siderTriggerElement.addEventListener("keydown", (e) =>
      handleKeyDown(e, siderTriggerElement)
    );

    return () => {
      siderTriggerElement.removeEventListener("mouseover", showCollapseTooltip);
      siderTriggerElement.removeEventListener("mouseout", hideCollapseTooltip);
      siderTriggerElement.removeEventListener("focusin", () =>
        showCollapseTooltip(collapseTooltip)
      );
      siderTriggerElement.removeEventListener("focusout", () =>
        hideCollapseTooltip(collapseTooltip)
      );
      siderTriggerElement.removeEventListener("keydown", (e) =>
        handleKeyDown(e, siderTriggerElement)
      );
    };
  }, []);

  const { profile } = React.useContext(AuthContext);

  /**
   * Create menu selectedKeys
   * @returns
   */
  const menuSelectedKeys = () => {
    let path = location?.pathname.split("/");
    if (path.includes("meetings") && path.includes("tasks"))
      path = path.filter((p) => p !== "tasks");

    if (
      path.length > 2 &&
      path[2]?.toLowerCase() != "settings" &&
      path[2]?.toLowerCase() != "logs" &&
      path[2]?.toLowerCase() != "search" &&
      path[2]?.toLowerCase() != "users" &&
      path[2]?.toLowerCase() != "manage"
    )
      return [path[2].toLowerCase()];

    let last = path[path.length - 1];
    let id = parseInt(last);

    if (id != NaN && id >= 0) return [""];

    return [last];
  };

  return (
    <Sider
      width="250px"
      collapsible
      collapsed={collapsed}
      onCollapse={(val) => setCollapsed(val)}
      breakpoint="xl"
    >
      <Layout style={{ minHeight: "100vh" }}>
        <Menu mode="inline" selectedKeys={menuSelectedKeys()}>
          {collapsed ? (
            <>
              <Menu.Item
                key="dashboard"
                icon={<FontAwesomeIcon icon={faHome} alt="faHome" />}
              >
                <Tooltip
                  title="Manage dashboard"
                  trigger={["focus", "hover"]}
                  placement="right"
                >
                  <Link
                    to={`/admin/dashboard`}
                    style={{ width: "18px" }}
                  ></Link>
                </Tooltip>
              </Menu.Item>
              <>
                <Menu.SubMenu
                  key="links"
                  title="Quick Links"
                  icon={addCheveron(
                    collapsed,
                    <FontAwesomeIcon icon={faLink} alt="faLink" />
                  )}
                >
                  <Menu.Item key="demo">
                    {" "}
                    <Link to={`/admin/search/demo`}> Demo Companies </Link>
                  </Menu.Item>
                  <Menu.Item key="trial">
                    {" "}
                    <Link to={`/admin/search/trial`}> In-Trial Companies </Link>
                  </Menu.Item>
                  <Menu.Item key="disabled">
                    {" "}
                    <Link to={`/admin/search/disabled`}>
                      {" "}
                      Disabled Companies{" "}
                    </Link>
                  </Menu.Item>
                  <Menu.Item key="parentchild">
                    {" "}
                    <Link to={`/admin/search/parentchild`}>
                      {" "}
                      Parent/Child (Legacy){" "}
                    </Link>
                  </Menu.Item>
                  <Menu.Item key="access">
                    {" "}
                    <Link to={`/admin/search/access`}> Granted Access </Link>
                  </Menu.Item>
                  <Menu.Item key="delete">
                    {" "}
                    <Link to={`/admin/search/delete`}> Ready to Delete </Link>
                  </Menu.Item>
                  <Menu.Item key="gabii">
                    {" "}
                    <Link to={`/admin/gabii`}> Gabii Report </Link>
                  </Menu.Item>
                  {HasRight([Rights.CSDev]) && (
                    <Menu.Item key="skynet">
                      {" "}
                      <Link to={`/admin/skynet`}> Skynet </Link>
                    </Menu.Item>
                  )}
                  {HasRight([Rights.CSAdmin]) && (
                    <Menu.Item key="defaultUser">
                      {" "}
                      <Link to={`/admin/defaultUser`}> Default User </Link>
                    </Menu.Item>
                  )}
                  {(HasRight([Rights.CSSuperUser]) || HasRight([Rights.CSAuthorizer])) && (
                    <Menu.Item key="deletionManagement">
                      {" "}
                      <Link to={`/admin/companyDeletionManagement`}>Company Deletion Management </Link>
                    </Menu.Item>
                  )}
                </Menu.SubMenu>
              </>
              <Menu.Item
                key="meetings"
                icon={
                  <FontAwesomeIcon icon={faCalendarAlt} alt="faCalendarAlt" />
                }
              >
                <Tooltip
                  title="Meetings"
                  trigger={["focus", "hover"]}
                  placement="right"
                >
                  <Link to={`/admin/meetings`} style={{ width: "18px" }}></Link>
                </Tooltip>
              </Menu.Item>
              <Menu.Item
                key="export"
                icon={
                  <FontAwesomeIcon
                    icon={faCloudArrowDown}
                    alt="faCloudArrowDown"
                  />
                }
              >
                <Tooltip
                  title="Export Users"
                  trigger={["focus", "hover"]}
                  placement="right"
                >
                  <Link
                    to={`/admin/users/export`}
                    style={{ width: "18px" }}
                  ></Link>
                </Tooltip>
              </Menu.Item>
              {HasRight([Rights.CSAdmin]) && (
                <>
                  <Menu.SubMenu
                    key="logsMenu"
                    title="Logs"
                    icon={addCheveron(
                      collapsed,
                      <FontAwesomeIcon
                        icon={faMemoCircleInfo}
                        alt="faMemoCircleInfo"
                      />
                    )}
                  >
                    <Menu.Item key="error">
                      <Tooltip
                        title="Manage Error Logs"
                        trigger={["focus", "hover"]}
                        placement="right"
                      >
                        <Link
                          to={`/admin/logs/error`}
                          style={{ width: "18px" }}
                        >
                          Error Logs
                        </Link>
                      </Tooltip>
                    </Menu.Item>
                    <Menu.Item key="cs">
                      <Tooltip
                        title="Manage Access Logs"
                        trigger={["focus", "hover"]}
                        placement="right"
                      >
                        <Link to={`/admin/logs/cs`} style={{ width: "18px" }}>
                          CS Logs
                        </Link>
                      </Tooltip>
                    </Menu.Item>
                    <Menu.Item key="cpq">
                      <Tooltip
                        title="CPQ Sync Logs"
                        trigger={["focus", "hover"]}
                        placement="right"
                      >
                        <Link to={`/admin/logs/cpq`} style={{ width: "18px" }}>
                          CPQ Sync Logs
                        </Link>
                      </Tooltip>
                    </Menu.Item>
                  </Menu.SubMenu>
                  <Menu.Item
                    key="products"
                    icon={<FontAwesomeIcon icon={faTag} alt="faTag" />}
                  >
                    <Tooltip
                      title="Manage Products"
                      trigger={["focus", "hover"]}
                      placement="right"
                    >
                      <Link
                        to={`/admin/products`}
                        style={{ width: "18px" }}
                      ></Link>
                    </Tooltip>
                  </Menu.Item>
                  <Menu.Item
                    key="manage"
                    icon={<FontAwesomeIcon icon={faUser} alt="faUser" />}
                  >
                    <Tooltip
                      title="Manage Users"
                      trigger={["focus", "hover"]}
                      placement="right"
                    >
                      <Link
                        to={`/admin/users/manage`}
                        style={{ width: "18px" }}
                      ></Link>
                    </Tooltip>
                  </Menu.Item>
                  <Menu.Item
                    key="indexing"
                    icon={
                      <FontAwesomeIcon icon={faListCheck} alt="faListCheck" />
                    }
                  >
                    <Tooltip
                      title="Manage Indexing"
                      trigger={["focus", "hover"]}
                      placement="right"
                    >
                      <Link
                        to={`/admin/manage/indexing`}
                        style={{ width: "18px" }}
                      ></Link>
                    </Tooltip>
                  </Menu.Item>
                </>
              )}
            </>
          ) : (
            <>
              {/* Start Of expanded */}
              <Menu.Item
                key="dashboard"
                icon={<FontAwesomeIcon icon={faHome} alt="faHome" />}
              >
                <Link to={`/admin/dashboard`} style={{ width: "18px" }}>
                  Dashboard
                </Link>
              </Menu.Item>
              <>
                <Menu.SubMenu
                  key="links"
                  title="Quick Links"
                  icon={addCheveron(
                    collapsed,
                    <FontAwesomeIcon icon={faLink} alt="faLink" />
                  )}
                >
                  <Menu.Item key="demo">
                    {" "}
                    <Link to={`/admin/search/demo`}> Demo Companies </Link>
                  </Menu.Item>
                  <Menu.Item key="trial">
                    {" "}
                    <Link to={`/admin/search/trial`}> In-Trial Companies </Link>
                  </Menu.Item>
                  <Menu.Item key="disabled">
                    {" "}
                    <Link to={`/admin/search/disabled`}>
                      {" "}
                      Disabled Companies{" "}
                    </Link>
                  </Menu.Item>
                  <Menu.Item key="parentchild">
                    {" "}
                    <Link to={`/admin/search/parentchild`}>
                      {" "}
                      Parent/Child (Legacy){" "}
                    </Link>
                  </Menu.Item>
                  <Menu.Item key="access">
                    {" "}
                    <Link to={`/admin/search/access`}> Granted Access </Link>
                  </Menu.Item>
                  <Menu.Item key="delete">
                    {" "}
                    <Link to={`/admin/search/delete`}> Ready to Delete </Link>
                  </Menu.Item>
                  <Menu.Item key="gabii">
                    {" "}
                    <Link to={`/admin/gabii`}> Gabii Report </Link>
                  </Menu.Item>
                  {HasRight([Rights.CSDev]) && (
                    <Menu.Item key="skynet">
                      {" "}
                      <Link to={`/admin/skynet`}> Skynet </Link>
                    </Menu.Item>
                  )}
                  {HasRight([Rights.CSAdmin]) && (
                    <Menu.Item key="defaultUser">
                      {" "}
                      <Link to={`/admin/defaultUser`}> Default User </Link>
                    </Menu.Item>
                  )}
                  {(HasRight([Rights.CSSuperUser]) || HasRight([Rights.CSAuthorizer])) && (
                    <Menu.Item key="deletionManagement">
                      {" "}
                      <Link to={`/admin/companyDeletionManagement`}>Company Deletion Management </Link>
                    </Menu.Item>
                  )}
                </Menu.SubMenu>
              </>
              <Menu.Item
                key="meetings"
                icon={
                  <FontAwesomeIcon icon={faCalendarAlt} alt="faCalendarAlt" />
                }
              >
                <Link to={`/admin/meetings`} style={{ width: "18px" }}>
                  Meetings
                </Link>
              </Menu.Item>
              <Menu.Item
                key="export"
                icon={
                  <FontAwesomeIcon
                    icon={faCloudArrowDown}
                    alt="faCloudArrowDown"
                  />
                }
              >
                <Link to={`/admin/users/export`} style={{ width: "18px" }}>
                  User Export
                </Link>
              </Menu.Item>
              {HasRight([Rights.CSAdmin]) && (
                <>
                  <Menu.SubMenu
                    key="logsMenu"
                    title="Logs"
                    icon={addCheveron(
                      collapsed,
                      <FontAwesomeIcon
                        icon={faMemoCircleInfo}
                        alt="faMemoCircleInfo"
                      />
                    )}
                  >
                    <Menu.Item key="error">
                      <Link to={`/admin/logs/error`} style={{ width: "18px" }}>
                        Error Logs
                      </Link>
                    </Menu.Item>
                    <Menu.Item key="cs">
                      <Link to={`/admin/logs/cs`} style={{ width: "18px" }}>
                        CS Logs
                      </Link>
                    </Menu.Item>
                    <Menu.Item key="cpq">
                      <Link to={`/admin/logs/cpq`} style={{ width: "18px" }}>
                        CPQ Sync Logs
                      </Link>
                    </Menu.Item>
                  </Menu.SubMenu>
                  <Menu.Item
                    key="products"
                    icon={<FontAwesomeIcon icon={faTag} alt="faTag" />}
                  >
                    <Link to={`/admin/products`} style={{ width: "18px" }}>
                      Products
                    </Link>
                  </Menu.Item>
                  <Menu.Item
                    key="manage"
                    icon={<FontAwesomeIcon icon={faUser} alt="faUser" />}
                  >
                    <Link to={`/admin/users/manage`} style={{ width: "18px" }}>
                      Manage Users
                    </Link>
                  </Menu.Item>
                  {HasRight([Rights.CSAdmin]) && (
                    <Menu.Item
                      key="indexing"
                      icon={
                        <FontAwesomeIcon icon={faListCheck} alt="faListCheck" />
                      }
                    >
                      <Link
                        to={`/admin/manage/indexing`}
                        style={{ width: "18px" }}
                      >
                        Manage Indexing
                      </Link>
                    </Menu.Item>
                  )}
                </>
              )}
            </>
          )}
        </Menu>
      </Layout>
      <div className={`collapse-tooltip ${collapsed ? "collapsed" : ""}`}>
        <div>
          <div className="ant-tooltip ant-tooltip-placement-right">
            <div className="ant-tooltip-content">
              <div className="ant-tooltip-arrow">
                <span className="ant-tooltip-arrow-content"></span>
              </div>
              <div className="ant-tooltip-inner" role="tooltip">
                {collapsed ? "Expand menu" : "Collapse menu"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Sider>
  );
};

LeftNav.defaultProps = defaultProps;

export default LeftNav;
